'use client';

import { Typography } from '@musinsa/mds';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { curatorQueries } from '../../query-factory';
import type { NicknameForm } from './NicknameEditForm';

interface Props {
  className?: string;
  errorMessage: string | null;
}

export const NicknameStatusDisplay = ({ className, errorMessage }: Props) => {
  const { watch } = useFormContext<NicknameForm>();
  const nickname = watch('nickname');
  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';
  const [profileQuery, availableCountQuery] = useQueries({
    queries: [
      {
        queryKey: curatorQueries.profile.profileBySnsTitle(snsTitle).queryKey,
        queryFn: curatorQueries.profile.profileBySnsTitle(snsTitle).queryFn,
        enabled: Boolean(snsTitle),
      },
      {
        queryKey: curatorQueries.profile.nicknameAvailableCount.queryKey,
        queryFn: curatorQueries.profile.nicknameAvailableCount.queryFn,
      },
    ],
  });

  const currentNickname = profileQuery.data?.nickname;
  const remainingCount = availableCountQuery.data?.remainingCount;

  // 우선순위에 따라 메시지 결정
  const getStatusMessage = () => {
    // 1. 에러 메시지가 있으면 최우선
    if (errorMessage) {
      return {
        message: errorMessage,
        colorScheme: 'red' as const,
      };
    }

    // 2. 수정 가능 횟수가 0이면
    if (remainingCount === 0) {
      return {
        message: '다음달에 수정 가능해요',
      };
    }

    // 3. 닉네임을 입력했고, 유효한 닉네임이면(1의 에러메시지 처리에서 검증되었음)
    if (nickname?.trim()) {
      return {
        message: '멋진 닉네임이네요!',
      };
    }

    // 4. 아무것도 입력하지 않았을 때: 현재 닉네임 표시
    if (currentNickname) {
      return {
        message: `현재 닉네임: ${currentNickname}`,
      };
    }

    // 기본값 : 빈 문자열
    return {
      message: '',
    };
  };

  const { message, colorScheme } = getStatusMessage();

  return (
    <Typography
      className={className}
      variant="textEtc11pxReg"
      colorScheme={colorScheme ?? 'gray-500'}
    >
      {message}
    </Typography>
  );
};
